import React, { useEffect, useState } from "react";
import { Container, Card, Table, Spinner, Alert, Badge, Button, ProgressBar, Form, Pagination, Modal } from "react-bootstrap";
import firebase from "firebase/app";
import "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import { useNavigate } from "react-router-dom";

/** Set to true to show the "Hosts Frenquency by Year" table again. */
const SHOW_HOSTS_FREQUENCY_BY_YEAR = false;

/** Default placeholder org name excluded from host name lists / exports. */
const EXCLUDED_HOST_DISPLAY_NAME = "New Organization";

const SAMPLE_FIRST_NAMES = [
  "Maya", "Jordan", "Alex", "Riley", "Morgan", "Casey", "Jamie", "Devon", "Priya", "Marcus",
  "Elena", "Noah", "Sofia", "Hiro", "Aisha", "Quinn", "Diego", "Lin", "Amara", "Ethan",
  "Zoe", "Omar", "Nina", "Leo", "Grace",
];
const SAMPLE_LAST_NAMES = [
  "Patel", "Nakamura", "Garcia", "Okafor", "Lindstrom", "Murphy", "Kim", "Santos", "Ibrahim",
  "Nguyen", "Silva", "Cohen", "Park", "Hernandez", "Okonkwo", "Foster", "Reyes", "Bauer",
  "Tanaka", "Kowalski", "Dubois", "Sharma", "Walsh", "Yamamoto", "Costa",
];

const SAMPLE_MIDDLE_INITIALS = ["M.", "J.", "L.", "R.", "S.", "K.", "A.", "D.", "T.", "E."];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const shuffleInPlace = (arr) => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

/** Fictional people's names only (not tied to Firestore). */
const generate35RandomHostNames = () => {
  const out = new Set();
  let attempts = 0;
  while (out.size < 35 && attempts < 800) {
    attempts += 1;
    const first = pick(SAMPLE_FIRST_NAMES);
    const last = pick(SAMPLE_LAST_NAMES);
    const name =
      Math.random() < 0.28
        ? `${first} ${pick(SAMPLE_MIDDLE_INITIALS)} ${last}`
        : `${first} ${last}`;
    const trimmed = name.replace(/\s+/g, " ").trim();
    if (!trimmed || trimmed === EXCLUDED_HOST_DISPLAY_NAME) continue;
    out.add(trimmed);
  }
  const arr = Array.from(out);
  shuffleInPlace(arr);
  return arr.slice(0, 35);
};

export default function AdminOrganizationOverview() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [allOrgs, setAllOrgs] = useState([]);
  const [updatingOrgId, setUpdatingOrgId] = useState(null);
  const [updatingEventDetailsId, setUpdatingEventDetailsId] = useState(null);
  const [bulkUpdatingEventDetails, setBulkUpdatingEventDetails] = useState(false);
  const [bulkEventDetailsProgress, setBulkEventDetailsProgress] = useState({ current: 0, total: 0 });
  const [cardCollapsed, setCardCollapsed] = useState({
    ltv: false,
    distribution: false,
    orgView: false,
    allOrgs: false,
  });
  const [ltvSortBy, setLtvSortBy] = useState("value");
  const [ltvSortDesc, setLtvSortDesc] = useState(false);
  const [allOrgsPage, setAllOrgsPage] = useState(1);
  const allOrgsPageSize = 25;
  const [selectedFirstEventMonthKey, setSelectedFirstEventMonthKey] = useState(null);
  const [selectedHostMonthKey, setSelectedHostMonthKey] = useState(null);
  const [selectedMonthForActivePopup, setSelectedMonthForActivePopup] = useState(null);
  const [selectedYearForEventsPopup, setSelectedYearForEventsPopup] = useState(null);
  const [sampleHostNames35, setSampleHostNames35] = useState([]);

  useEffect(() => {
    checkAdmin();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin) {
      loadOrganizations();
    }
  }, [isAdmin]);

  const checkAdmin = async () => {
    try {
      if (!currentUser) {
        navigate("/");
        return;
      }
      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      const roles = adminDoc.data()?.roles || [];
      if (!roles.includes("management")) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      setIsAdmin(true);
      setCheckingAdmin(false);
    } catch (err) {
      console.error("Admin check failed:", err);
      setError("Failed to verify admin access");
      setIsAdmin(false);
      setCheckingAdmin(false);
    }
  };

  const loadOrganizations = async () => {
    setLoading(true);
    setError("");
    try {
      const snapshot = await fires.collection("groups").get();

      const mapped = snapshot.docs
        .map((doc) => {
          const data = doc.data() || {};
          const rawCount = data.event_count;
          const eventCount =
            typeof rawCount === "number"
              ? rawCount
              : rawCount != null
              ? Number(rawCount) || 0
              : 0;
          const ts = data.timestamp?.toDate?.() ?? (data.createdAt?.toDate?.() ?? data.createdAt);
          const createdAt =
            ts instanceof Date ? ts : ts ? new Date(ts) : null;
          const ltv = data.lifetimeValue;
          const lifetimeValue =
            ltv && typeof ltv.value === "number"
              ? Math.round(ltv.value * 100) / 100
              : ltv && ltv.value != null
              ? Math.round(Number(ltv.value) * 100) / 100
              : null;
          const ltvTimestamp =
            ltv?.timestamp?.toDate?.() ?? (ltv?.timestamp instanceof Date ? ltv.timestamp : null);
          const lastEventCreatedAt =
            ltv?.lastEventCreatedAt?.toDate?.() ??
            (ltv?.lastEventCreatedAt instanceof Date ? ltv.lastEventCreatedAt : null);
          const lastEventId = ltv?.lastEventId ?? null;
          const lastEventTitle = ltv?.lastEventTitle ?? null;
          const firstEventCreatedAt =
            ltv?.firstEventCreatedAt?.toDate?.() ??
            (ltv?.firstEventCreatedAt instanceof Date ? ltv.firstEventCreatedAt : null);
          const firstEventId = ltv?.firstEventId ?? null;
          const firstEventTitle = ltv?.firstEventTitle ?? null;
          const allEvents = ltv?.allEvents ?? [];
          return {
            id: doc.id,
            name: data.name || data.orgName || data.groupName || "Unnamed organization",
            eventCount,
            createdAt,
            lifetimeValue: lifetimeValue != null || ltvTimestamp != null || lastEventCreatedAt != null || lastEventId != null || lastEventTitle != null
              ? {
                  value: lifetimeValue ?? null,
                  orders: ltv?.orders ?? null,
                  users: ltv?.users ?? null,
                  views: ltv?.views ?? null,
                  tickets: ltv?.tickets ?? null,
                  timestamp: ltvTimestamp,
                  lastEventCreatedAt: lastEventCreatedAt ?? null,
                  lastEventId,
                  lastEventTitle,
                  firstEventCreatedAt: firstEventCreatedAt ?? null,
                  firstEventId: firstEventId ?? null,
                  firstEventTitle: firstEventTitle ?? null,
                  allEvents: allEvents ?? null,
                }
              : null,
          };
        });

      const filtered = mapped
        .filter((org) => org.eventCount > 0)
        .sort((a, b) => b.eventCount - a.eventCount);

      const allSorted = mapped.slice().sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      });

      setOrgs(filtered);
      setAllOrgs(allSorted);
    } catch (err) {
      console.error("Error loading organizations:", err);
      setError(err?.message || "Failed to load organizations");
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const csvEscape = (value) => {
    const text = value == null ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const handleExportOrganizationView = () => {
    if (!orgs.length) return;
    const rows = orgs.map((org) => {
      const createdAt = org.createdAt instanceof Date && !isNaN(org.createdAt.getTime())
        ? org.createdAt.toISOString()
        : "";
      return [org.name || "Unnamed organization", createdAt];
    });
    const csvContent = [
      ["Name", "Creation Date"],
      ...rows,
    ]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `organization-view-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Distribution of organizations by event_count (>= thresholds), among orgs with at least 1 event
  const orgsWithEvents = allOrgs.filter((o) => (o.eventCount || 0) >= 1);

  const allHostNamesExcludingPlaceholder = React.useMemo(() => {
    const names = allOrgs
      .filter((o) => Number(o.eventCount ?? 0) >= 1)
      .filter((o) => (o.name || "").trim() !== EXCLUDED_HOST_DISPLAY_NAME)
      .map((o) => o.name || "Unnamed organization");
    const unique = [...new Set(names)];
    unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return unique;
  }, [allOrgs]);

  const handleExportAllHostNames = () => {
    if (!allHostNamesExcludingPlaceholder.length) return;
    const csvContent = [
      ["Name"],
      ...allHostNamesExcludingPlaceholder.map((name) => [name]),
    ]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `all-host-names-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGenerateSampleHostNames35 = () => {
    setSampleHostNames35(generate35RandomHostNames());
  };

  const handleExportSampleHostNames35 = () => {
    if (!sampleHostNames35.length) return;
    const csvContent = [
      ["Name"],
      ...sampleHostNames35.map((name) => [name]),
    ]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sample-host-names-35-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totalWithEvents = orgsWithEvents.length;
  const thresholds = [1, 5, 10, 25, 50];

  // Hosts by year:
  // - Only hosts with overall `event_count >= 1` are considered.
  // - For each year (2023-2026), a host counts in that year if it has >=1 event in
  //   `lifetimeValue.allEvents[*]` whose `timestamp` falls in that year.
  // - The buckets (<2, 2-5, >5) are based on the NUMBER of matching events in that year.
  const hostCountsByYearBuckets = React.useMemo(() => {
    const years = [2023, 2024, 2025, 2026];
    const afterRow = {
      year: "Total (event_count >= 1)",
      under2: 0,
      under2Users: 0,
      between2and5: 0,
      between2and5Users: 0,
      above5: 0,
      above5Users: 0,
      total: 0,
      totalUsers: 0,
    };
    const yearToRow = new Map(
      years.map((year) => [
        year,
        {
          year,
          under2: 0,
          under2Users: 0,
          between2and5: 0,
          between2and5Users: 0,
          above5: 0,
          above5Users: 0,
          total: 0,
          totalUsers: 0,
        },
      ])
    );

    allOrgs.forEach((org) => {
      const ec = Number(org.eventCount ?? 0);
      if (ec < 1) return; // Only care about hosts with event_count >= 1

      // Overall total buckets (no year constraint) - based purely on event_count >= 1
      afterRow.total += 1;

      const events = org.lifetimeValue?.allEvents || [];
      let allUsersTotal = 0;
      const usersInYear = new Map(); // year -> users sum
      const countInYear = new Map(); // year -> matching event count

      if (Array.isArray(events) && events.length > 0) {
        events.forEach((ev) => {
          const ts = ev?.timestamp;
          const d =
            ts instanceof Date
              ? ts
              : ts?.toDate?.()
              ? ts.toDate()
              : ts
              ? new Date(ts)
              : null;
          if (!d || isNaN(d.getTime())) return;

          const users = Number(ev?.users ?? 0) || 0;
          allUsersTotal += users;

          const y = d.getFullYear();
          if (!years.includes(y)) return;
          if (!usersInYear.has(y)) usersInYear.set(y, 0);
          if (!countInYear.has(y)) countInYear.set(y, 0);
          usersInYear.set(y, (usersInYear.get(y) || 0) + users);
          countInYear.set(y, (countInYear.get(y) || 0) + 1);
        });
      }

      // Users for total row: sum of users across all events (regardless of year).
      afterRow.totalUsers += allUsersTotal;

      if (ec < 2) afterRow.under2 += 1; // 1 event
      else if (ec <= 4) afterRow.between2and5 += 1; // 2-4 events
      else afterRow.above5 += 1; // >= 5 events
      // Bucket users on total row using the same event_count bucket as hosts.
      if (ec < 2) afterRow.under2Users += allUsersTotal;
      else if (ec <= 4) afterRow.between2and5Users += allUsersTotal;
      else afterRow.above5Users += allUsersTotal;

      years.forEach((year) => {
        const row = yearToRow.get(year);
        if (!row) return;
        const matchingCount = countInYear.get(year) || 0;
        if (matchingCount <= 0) return;

        const matchingUsers = Number(usersInYear.get(year) || 0) || 0;
        row.total += 1;
        row.totalUsers += matchingUsers;
        if (matchingCount < 2) row.under2 += 1;
        else if (matchingCount <= 4) row.between2and5 += 1; // 2-4 events
        else row.above5 += 1; // >= 5 events

        // Bucket users on the year rows using the same matching event count category as hosts.
        if (matchingCount < 2) row.under2Users += matchingUsers;
        else if (matchingCount <= 4) row.between2and5Users += matchingUsers;
        else row.above5Users += matchingUsers;
      });
    });

    return [...years.map((y) => yearToRow.get(y)), afterRow];
  }, [allOrgs]);

  const hostsAndEventsForSelectedYear = React.useMemo(() => {
    if (!selectedYearForEventsPopup) return [];
    const year = selectedYearForEventsPopup;

    return orgsWithEvents
      .map((org) => {
        const events = org.lifetimeValue?.allEvents || [];
        if (!Array.isArray(events) || events.length === 0) return null;

        const matchingEvents = events
          .map((ev) => {
            const ts = ev?.timestamp;
            const d =
              ts instanceof Date
                ? ts
                : ts?.toDate?.()
                ? ts.toDate()
                : ts
                ? new Date(ts)
                : null;

            if (!d || isNaN(d.getTime())) return null;
            if (d.getFullYear() !== year) return null;

            return {
              eventId: ev?.id ?? ev?.eventId ?? "—",
              title:
                ev?.title ??
                ev?.eventTitle ??
                ev?.eventDetails?.title ??
                "Untitled event",
              timestamp: d,
              users: Number(ev?.users ?? 0) || 0,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        if (matchingEvents.length === 0) return null;
        const usersInYear = matchingEvents.reduce(
          (sum, ev) => sum + (Number(ev.users ?? 0) || 0),
          0
        );

        return {
          orgId: org.id,
          orgName: org.name || org.id || "—",
          events: matchingEvents,
          usersInYear,
        };
      })
      .filter(Boolean);
  }, [orgsWithEvents, selectedYearForEventsPopup]);

  const totalEventsForSelectedYear = React.useMemo(() => {
    if (!selectedYearForEventsPopup) return 0;
    return hostsAndEventsForSelectedYear.reduce(
      (sum, host) => sum + (host.events?.length ?? 0),
      0
    );
  }, [hostsAndEventsForSelectedYear, selectedYearForEventsPopup]);

  const totalUsersForSelectedYear = React.useMemo(() => {
    if (!selectedYearForEventsPopup) return 0;
    return hostsAndEventsForSelectedYear.reduce(
      (sum, host) => sum + (Number(host.usersInYear ?? 0) || 0),
      0
    );
  }, [hostsAndEventsForSelectedYear, selectedYearForEventsPopup]);

  const topHostByUsersForSelectedYear = React.useMemo(() => {
    if (!selectedYearForEventsPopup) return null;
    if (!hostsAndEventsForSelectedYear.length) return null;
    return hostsAndEventsForSelectedYear
      .slice()
      .sort((a, b) => {
        const au = Number(a.usersInYear ?? 0) || 0;
        const bu = Number(b.usersInYear ?? 0) || 0;
        if (bu !== au) return bu - au;
        const ae = a.events?.length ?? 0;
        const be = b.events?.length ?? 0;
        return be - ae;
      })[0];
  }, [hostsAndEventsForSelectedYear, selectedYearForEventsPopup]);

  const eventCountDistribution = thresholds.map((min) => {
    const count = orgsWithEvents.filter((o) => (o.eventCount || 0) >= min).length;
    const percent = totalWithEvents ? (count / totalWithEvents) * 100 : 0;
    return { min, count, percent };
  });

  // Active organizations by month: use lifetimeValue.allEvents timestamps
  const activeOrgByMonth = React.useMemo(() => {
    if (!orgsWithEvents.length) return [];
    const map = new Map();
    orgsWithEvents.forEach((org) => {
      const events = org.lifetimeValue?.allEvents || [];
      if (!Array.isArray(events) || events.length === 0) return;
      const monthKeys = new Set();
      events.forEach((ev) => {
        const ts = ev?.timestamp;
        const d =
          ts instanceof Date
            ? ts
            : ts?.toDate?.()
            ? ts.toDate()
            : ts
            ? new Date(ts)
            : null;
        if (!d || isNaN(d.getTime())) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
        monthKeys.add(key);
      });
      if (!monthKeys.size) return;
      // Compute firstEventCreatedAt month key to know "new host" month
      let createdKey = null;
      const firstCreated = org.lifetimeValue?.firstEventCreatedAt;
      if (firstCreated) {
        const cd =
          firstCreated instanceof Date
            ? firstCreated
            : firstCreated?.toDate?.()
            ? firstCreated.toDate()
            : firstCreated
            ? new Date(firstCreated)
            : null;
        if (cd && !isNaN(cd.getTime())) {
          createdKey = `${cd.getFullYear()}-${String(cd.getMonth() + 1).padStart(
            2,
            "0"
          )}`;
        }
      }
      monthKeys.forEach((key) => {
        const sampleDateParts = key.split("-");
        const year = Number(sampleDateParts[0]);
        const monthIdx = Number(sampleDateParts[1]) - 1;
        const sampleDate = new Date(year, monthIdx, 1);
        const label = sampleDate.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
        });
        const prev =
          map.get(key) || {
            key,
            label,
            orgsTotal: 0,
            orgs: 0,
          };
        // Raw: org had at least one event in this month
        prev.orgsTotal += 1;
        // Minus overlaps: only if this month is NOT the same as createdAt month
        if (!createdKey || createdKey !== key) {
          prev.orgs += 1;
        }
        map.set(key, prev);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [orgsWithEvents]);

  const orgsForSelectedFirstEventMonth = React.useMemo(() => {
    if (!selectedFirstEventMonthKey) return [];
    return orgsWithEvents.filter((org) => {
      const events = org.lifetimeValue?.allEvents || [];
      if (!Array.isArray(events) || events.length === 0) return false;
      let hasInMonth = false;
      events.forEach((ev) => {
        const ts = ev?.timestamp;
        const d =
          ts instanceof Date
            ? ts
            : ts?.toDate?.()
            ? ts.toDate()
            : ts
            ? new Date(ts)
            : null;
        if (!d || isNaN(d.getTime())) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
        if (key === selectedFirstEventMonthKey) {
          hasInMonth = true;
        }
      });
      if (!hasInMonth) return false;
      // Exclude overlaps: do not include orgs whose firstEventCreatedAt month is the same
      const firstCreated = org.lifetimeValue?.firstEventCreatedAt;
      if (firstCreated) {
        const cd =
          firstCreated instanceof Date
            ? firstCreated
            : firstCreated?.toDate?.()
            ? firstCreated.toDate()
            : firstCreated
            ? new Date(firstCreated)
            : null;
        if (cd && !isNaN(cd.getTime())) {
          const createdKey = `${cd.getFullYear()}-${String(cd.getMonth() + 1).padStart(
            2,
            "0"
          )}`;
          if (createdKey === selectedFirstEventMonthKey) {
            return false;
          }
        }
      }
      return true;
    });
  }, [orgsWithEvents, selectedFirstEventMonthKey]);

  // Monthly host additions (organizations with eventCount >= 1), grouped by firstEventCreatedAt month
  const monthlyHostDistribution = (() => {
    if (!orgsWithEvents.length) return [];
    const map = new Map();
    orgsWithEvents.forEach((org) => {
      const firstCreated = org.lifetimeValue?.firstEventCreatedAt;
      const d =
        firstCreated instanceof Date
          ? firstCreated
          : firstCreated?.toDate?.()
          ? firstCreated.toDate()
          : firstCreated
          ? new Date(firstCreated)
          : null;
      if (!d || isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString(undefined, { year: "numeric", month: "short" });
      const prev = map.get(key) || { key, label, count: 0 };
      prev.count += 1;
      map.set(key, prev);
    });
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  })();

  // Cumulative host count up to (and including) each month.
  // This is a running sum of "new hosts" where "new host month" comes from firstEventCreatedAt.
  const monthlyHostDistributionWithCumulative = React.useMemo(() => {
    let running = 0;
    return monthlyHostDistribution.map((row) => {
      running += Number(row.count ?? 0);
      return { ...row, totalHostsToMonth: running };
    });
  }, [monthlyHostDistribution]);

  const orgsForSelectedHostMonth = React.useMemo(() => {
    if (!selectedHostMonthKey) return [];
    return orgsWithEvents.filter((org) => {
      const firstCreated = org.lifetimeValue?.firstEventCreatedAt;
      const d =
        firstCreated instanceof Date
          ? firstCreated
          : firstCreated?.toDate?.()
          ? firstCreated.toDate()
          : firstCreated
          ? new Date(firstCreated)
          : null;
      if (!d || isNaN(d.getTime())) return false;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      return key === selectedHostMonthKey;
    });
  }, [orgsWithEvents, selectedHostMonthKey]);

  // Active hosts for the popup: orgs that have at least one event in the selected month (from allEvents)
  const activeHostsListForPopup = React.useMemo(() => {
    if (!selectedMonthForActivePopup) return [];
    return orgsWithEvents
      .filter((org) => {
        const events = org.lifetimeValue?.allEvents || [];
        if (!Array.isArray(events) || events.length === 0) return false;
        for (let i = 0; i < events.length; i++) {
          const ev = events[i];
          const ts = ev?.timestamp;
          const d =
            ts instanceof Date
              ? ts
              : ts?.toDate?.()
              ? ts.toDate()
              : ts
              ? new Date(ts)
              : null;
          if (!d || isNaN(d.getTime())) continue;
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}`;
          if (key === selectedMonthForActivePopup) return true;
        }
        return false;
      })
      .map((org) => ({
        id: org.id,
        name: org.name || org.id || "—",
      }));
  }, [orgsWithEvents, selectedMonthForActivePopup]);

  // New hosts for the popup: orgs whose firstEventCreatedAt month matches the selected month
  const newHostsListForPopup = React.useMemo(() => {
    if (!selectedMonthForActivePopup) return [];
    return orgsWithEvents
      .filter((org) => {
        const firstCreated = org.lifetimeValue?.firstEventCreatedAt;
        const d =
          firstCreated instanceof Date
            ? firstCreated
            : firstCreated?.toDate?.()
            ? firstCreated.toDate()
            : firstCreated
            ? new Date(firstCreated)
            : null;
        if (!d || isNaN(d.getTime())) return false;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
        return key === selectedMonthForActivePopup;
      })
      .map((org) => ({
        id: org.id,
        name: org.name || org.id || "—",
      }));
  }, [orgsWithEvents, selectedMonthForActivePopup]);

  const handleUpdateEventCount = async (org) => {
    if (!org?.id) return;
    setUpdatingOrgId(org.id);
    setError("");
    try {
      // Count events for this organization
      const eventsSnap = await fires
        .collection("events")
        .where("orgId", "==", org.id)
        .get();
      const newCount = eventsSnap.size;
      

      await fires.collection("groups").doc(org.id).update({
        event_count: newCount,
      });

      // Update local state
      setOrgs((prev) =>
        prev
          .map((o) =>
            o.id === org.id
              ? {
                  ...o,
                  eventCount: newCount,
                }
              : o
          )
          .filter((o) => o.eventCount > 0)
          .sort((a, b) => b.eventCount - a.eventCount)
      );
      setAllOrgs((prev) =>
        prev.map((o) =>
          o.id === org.id
            ? {
                ...o,
                eventCount: newCount,
              }
            : o
        )
      );
    } catch (err) {
      console.error("Error updating event_count:", err);
      setError(err?.message || "Failed to update event_count");
    } finally {
      setUpdatingOrgId(null);
    }
  };

  const handleUpdateEventDetails = async (org) => {
    if (!org?.id) return;
    setUpdatingEventDetailsId(org.id);
    setError("");
    try {
      const eventsSnap = await fires
        .collection("events")
        .where("orgId", "==", org.id)
        .get();

      const eventCount = eventsSnap.size;

      let totalOrderAmount = 0;
      let totalOrders = 0;
      let totalUsers = 0;
      let totalViews = 0;
      let totalTickets = 0;
      let lastEventCreatedAt = null;
      let firstEventCreatedAt = null;
      let lastEventId = null;
      let lastEventTitle = null;
      let firstEventId = null;
      let firstEventTitle = null;

      let allEvents = []

      eventsSnap.docs.forEach((doc) => {
        const data = doc.data() || {};
        const summary = data.summary || {};
        const timestamp = data.timestamp?.toDate?.() ?? (data.timestamp instanceof Date ? data.timestamp : data.timestamp ? new Date(data.timestamp) : null);
        const title = data.title || data.eventTitle || data.eventDetails?.title || "Untitled event";
        allEvents.push({
          id: doc.id,
          timestamp,
          title,
          users: summary.users || 0,
          tickets: summary.tickets || 0,
          views: summary.views || 0,
          orders: summary.orders || 0,
          orderTotal: summary.orderTotal || 0,
        });
        totalOrderAmount +=
          typeof summary.orderTotal === "number"
            ? summary.orderTotal
            : summary.orderTotal != null
            ? Number(summary.orderTotal) || 0
            : 0;
        totalOrders +=
          typeof summary.orders === "number"
            ? summary.orders
            : summary.orders != null
            ? Number(summary.orders) || 0
            : 0;
        totalUsers +=
          typeof summary.users === "number"
            ? summary.users
            : summary.users != null
            ? Number(summary.users) || 0
            : 0;
        totalViews +=
          typeof summary.views === "number"
            ? summary.views
            : summary.views != null
            ? Number(summary.views) || 0
            : 0;
        totalTickets +=
          typeof summary.tickets === "number"
            ? summary.tickets
            : summary.tickets != null
            ? Number(summary.tickets) || 0
            : 0;
        const eventTs =
          data.timestamp?.toDate?.() ??
          (data.timestamp instanceof Date
            ? data.timestamp
            : data.timestamp
            ? new Date(data.timestamp)
            : null);
        if (eventTs && !isNaN(eventTs.getTime())) {
          if (!firstEventCreatedAt || eventTs < firstEventCreatedAt) {
            firstEventCreatedAt = eventTs;
            firstEventId = doc.id;
            firstEventTitle =
              data.title ||
              data.eventTitle ||
              data.eventDetails?.title ||
              "Untitled event";
          }
          if (!lastEventCreatedAt || eventTs > lastEventCreatedAt) {
            lastEventCreatedAt = eventTs;
            lastEventId = doc.id;
            lastEventTitle =
              data.title ||
              data.eventTitle ||
              data.eventDetails?.title ||
              "Untitled event";
          }
        }
      });

      const lifetimeValue = Math.round(totalOrderAmount * 100) / 100;

      const now = new Date();
      const updatePayload = {
        value: lifetimeValue,
        orders: totalOrders,
        users: totalUsers,
        views: totalViews,
        tickets: totalTickets,
        allEvents: allEvents,
        timestamp: firebase.firestore.Timestamp.fromDate(now),
      };
      if (firstEventCreatedAt) {
        updatePayload.firstEventCreatedAt =
          firebase.firestore.Timestamp.fromDate(firstEventCreatedAt);
      }
      if (firstEventId) updatePayload.firstEventId = firstEventId;
      if (firstEventTitle != null) updatePayload.firstEventTitle = firstEventTitle;
      if (lastEventCreatedAt) {
        updatePayload.lastEventCreatedAt = firebase.firestore.Timestamp.fromDate(lastEventCreatedAt);
      }
      if (lastEventId) updatePayload.lastEventId = lastEventId;
      if (lastEventTitle != null) updatePayload.lastEventTitle = lastEventTitle;
      await fires.collection("groups").doc(org.id).update({
        lifetimeValue: updatePayload,
        event_count: eventCount,
      });

      const updatedOrg = {
        ...org,
        lifetimeValue: {
          value: lifetimeValue,
          orders: totalOrders,
          users: totalUsers,
          views: totalViews,
          tickets: totalTickets,
          timestamp: now,
          allEvents: allEvents,
          firstEventCreatedAt: firstEventCreatedAt || undefined,
          firstEventId: firstEventId || undefined,
          firstEventTitle: firstEventTitle ?? undefined,
          lastEventCreatedAt: lastEventCreatedAt || undefined,
          lastEventId: lastEventId || undefined,
          lastEventTitle: lastEventTitle ?? undefined,
        },
      };

      setOrgs((prev) =>
        prev.map((o) => (o.id === org.id ? updatedOrg : o))
      );
      setAllOrgs((prev) =>
        prev.map((o) => (o.id === org.id ? updatedOrg : o))
      );
    } catch (err) {
      console.error("Error updating event details / LTV:", err);
      setError(err?.message || "Failed to update event details");
    } finally {
      setUpdatingEventDetailsId(null);
    }
  };

  const orgsWithOneOrMoreEvents = allOrgs.filter((o) => (o.eventCount || 0) >= 1);

  const allOrgsTotalPages = Math.max(
    1,
    Math.ceil(allOrgs.length / allOrgsPageSize)
  );
  const allOrgsPageClamped = Math.min(allOrgsPage, allOrgsTotalPages);
  const allOrgsStartIndex = (allOrgsPageClamped - 1) * allOrgsPageSize;
  const allOrgsPageItems = allOrgs.slice(
    allOrgsStartIndex,
    allOrgsStartIndex + allOrgsPageSize
  );

  // Most lifetime value hosts: filter then sort by selected field
  const mostLtvHosts = React.useMemo(() => {
    const filtered = allOrgs.filter(
      (o) =>
        o.lifetimeValue != null &&
        (o.lifetimeValue.value != null ||
          o.lifetimeValue.users != null ||
          o.lifetimeValue.orders != null ||
          o.lifetimeValue.tickets != null ||
          o.lifetimeValue.views != null)
    );
    const dir = ltvSortDesc ? -1 : 1;
    const getVal = (org, key) => {
      if (key === "eventCount") return Number(org.eventCount ?? 0);
      const v = org.lifetimeValue?.[key];
      return key === "value" ? Number(v ?? 0) : Number(v ?? 0);
    };
    return filtered.slice().sort((a, b) => {
      const av = getVal(a, ltvSortBy);
      const bv = getVal(b, ltvSortBy);
      if (av !== bv) return (bv - av) * dir;
      return 0;
    });
  }, [allOrgs, ltvSortBy, ltvSortDesc]);

  const handleBulkUpdateEventDetails = async () => {
    if (orgsWithOneOrMoreEvents.length === 0) return;
    setError("");
    setBulkUpdatingEventDetails(true);
    setBulkEventDetailsProgress({ current: 0, total: orgsWithOneOrMoreEvents.length });
    try {
      for (let i = 0; i < orgsWithOneOrMoreEvents.length; i++) {
        await handleUpdateEventDetails(orgsWithOneOrMoreEvents[i]);
        setBulkEventDetailsProgress({ current: i + 1, total: orgsWithOneOrMoreEvents.length });
      }
    } catch (err) {
      console.error("Bulk update event details failed:", err);
    } finally {
      setBulkUpdatingEventDetails(false);
      setBulkEventDetailsProgress({ current: 0, total: 0 });
    }
  };

  const handleBulkUpdateEventDetailsAll = async () => {
    if (allOrgs.length === 0) return;
    setError("");
    setBulkUpdatingEventDetails(true);
    setBulkEventDetailsProgress({ current: 0, total: allOrgs.length });
    try {
      for (let i = 0; i < allOrgs.length; i++) {
        await handleUpdateEventDetails(allOrgs[i]);
        setBulkEventDetailsProgress({ current: i + 1, total: allOrgs.length });
      }
    } catch (err) {
      console.error("Bulk update event details (all) failed:", err);
    } finally {
      setBulkUpdatingEventDetails(false);
      setBulkEventDetailsProgress({ current: 0, total: 0 });
    }
  };

  if (checkingAdmin || !currentUser) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" />
          <span className="ms-2">Loading...</span>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Access denied. Admin only.</Alert>
        <Button variant="outline-primary" onClick={() => navigate("/")}>
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">Organization Overview</h2>
          <p className="text-muted mb-0">
            Organizations from <code>groups</code> with more than one event, plus a full list by created time.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={bulkUpdatingEventDetails || loading || orgsWithOneOrMoreEvents.length === 0}
            onClick={handleBulkUpdateEventDetails}
          >
            {bulkUpdatingEventDetails ? "Bulk updating…" : "Bulk update (1+ events)"}
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            disabled={bulkUpdatingEventDetails || loading || allOrgs.length === 0}
            onClick={handleBulkUpdateEventDetailsAll}
          >
            {bulkUpdatingEventDetails ? "Bulk updating…" : "Bulk update (all orgs)"}
          </Button>
        </div>
      </div>

      {bulkUpdatingEventDetails && bulkEventDetailsProgress.total > 0 && (
        <Card className="shadow-sm mb-3 border-primary">
          <Card.Body className="py-2 px-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="small fw-semibold">Bulk update event details</span>
              <span className="small text-muted">
                {bulkEventDetailsProgress.current} of {bulkEventDetailsProgress.total} organizations
                {bulkEventDetailsProgress.total > 0 && (
                  <span className="ms-1">
                    ({Math.round((bulkEventDetailsProgress.current / bulkEventDetailsProgress.total) * 100)}%)
                  </span>
                )}
              </span>
            </div>
            <ProgressBar
              now={
                bulkEventDetailsProgress.total > 0
                  ? (bulkEventDetailsProgress.current / bulkEventDetailsProgress.total) * 100
                  : 0
              }
              label={`${bulkEventDetailsProgress.current}/${bulkEventDetailsProgress.total}`}
              striped
              animated
              style={{ height: "1.25rem" }}
            />
          </Card.Body>
        </Card>
      )}

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="shadow-sm mb-4">
        <Card.Body className="py-3 px-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
            <div>
              <h6 className="mb-1">All host names</h6>
              <p className="text-muted mb-0 small">
                Organizations with <code>event_count ≥ 1</code>, excluding{" "}
                <code>{EXCLUDED_HOST_DISPLAY_NAME}</code>.{" "}
                <span className="text-nowrap">
                  ({allHostNamesExcludingPlaceholder.length} unique names)
                </span>
              </p>
            </div>
            <Button
              size="sm"
              variant="outline-success"
              disabled={loading || allHostNamesExcludingPlaceholder.length === 0}
              onClick={handleExportAllHostNames}
            >
              Export names (CSV)
            </Button>
          </div>
          {loading ? (
            <div className="text-muted small py-2">Loading…</div>
          ) : allHostNamesExcludingPlaceholder.length === 0 ? (
            <p className="text-muted small mb-0">No matching hosts.</p>
          ) : (
            <div
              className="border rounded small p-2 font-monospace bg-light"
              style={{ maxHeight: "280px", overflowY: "auto", whiteSpace: "pre-wrap" }}
            >
              {allHostNamesExcludingPlaceholder.join("\n")}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-4">
        <Card.Body className="py-3 px-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
            <div>
              <h6 className="mb-1">Sample: 35 random people&apos;s names</h6>
              <p className="text-muted mb-0 small">
                Fictional given + family names for demos or copy tests. Click generate for a new
                random set; not loaded from your database.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button size="sm" variant="outline-primary" onClick={handleGenerateSampleHostNames35}>
                Generate 35 names
              </Button>
              <Button
                size="sm"
                variant="outline-success"
                disabled={sampleHostNames35.length === 0}
                onClick={handleExportSampleHostNames35}
              >
                Export sample (CSV)
              </Button>
            </div>
          </div>
          {sampleHostNames35.length === 0 ? (
            <p className="text-muted small mb-0">Click &quot;Generate 35 names&quot; to create a list.</p>
          ) : (
            <div
              className="border rounded small p-2 font-monospace bg-light"
              style={{ maxHeight: "280px", overflowY: "auto", whiteSpace: "pre-wrap" }}
            >
              {sampleHostNames35.join("\n")}
            </div>
          )}
        </Card.Body>
      </Card>

      {SHOW_HOSTS_FREQUENCY_BY_YEAR && (
      <Card className="shadow-sm mb-4">
          <div className="d-flex align-items-center gap-2 py-2 px-3 border-bottom">
          <h6 className="mb-0">Hosts Frenquency by Year</h6>
        </div>
        <Card.Body className="py-3 px-3">
          <p className="text-muted mb-2 small">
            Hosts with <code>event_count &gt;= 1</code> are considered. For each year, the host
            bucket (<code>&lt;2</code>, <code>2-4</code>, <code>&gt;= 5</code>) is based on the
            number of matching events in <code>lifetimeValue.allEvents[*]</code> whose
            <code>timestamp</code> falls in that year.
          </p>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Year</th>
                  <th className="text-end">&lt; 2 events</th>
                  <th className="text-end">&lt; 2 users</th>
                  <th className="text-end">2 - 4 events</th>
                  <th className="text-end">2 - 4 users</th>
                  <th className="text-end">&gt;= 5 events</th>
                  <th className="text-end">&gt;= 5 users</th>
                  <th className="text-end">Total hosts</th>
                </tr>
              </thead>
              <tbody>
                {hostCountsByYearBuckets.map((row) => {
                  const isYearRow = typeof row.year === "number";
                  const isSelected = isYearRow && selectedYearForEventsPopup === row.year;
                  const rowTotal = Number(row.total ?? 0) || 0;
                  const under2Pct = rowTotal ? (Number(row.under2 ?? 0) / rowTotal) * 100 : 0;
                  const betweenPct = rowTotal
                    ? (Number(row.between2and5 ?? 0) / rowTotal) * 100
                    : 0;
                  const abovePct = rowTotal
                    ? (Number(row.above5 ?? 0) / rowTotal) * 100
                    : 0;
                  return (
                    <tr
                      key={row.year}
                      style={{
                        cursor: isYearRow ? "pointer" : "default",
                        backgroundColor: isSelected
                          ? "rgba(13, 110, 253, 0.05)"
                          : undefined,
                      }}
                      onClick={() => {
                        if (!isYearRow) return;
                        setSelectedMonthForActivePopup(null); // avoid overlapping popups
                        setSelectedYearForEventsPopup((prev) =>
                          prev === row.year ? null : row.year
                        );
                      }}
                    >
                      <td>{row.year}</td>
                      <td className="text-end">
                        {row.under2} ({under2Pct.toFixed(1)}%)
                      </td>
                      <td className="text-end">
                        {Number(row.under2Users ?? 0) || 0}
                      </td>
                      <td className="text-end">
                        {row.between2and5} ({betweenPct.toFixed(1)}%)
                      </td>
                      <td className="text-end">
                        {Number(row.between2and5Users ?? 0) || 0}
                      </td>
                      <td className="text-end">
                        {row.above5} ({abovePct.toFixed(1)}%)
                      </td>
                      <td className="text-end">{Number(row.above5Users ?? 0) || 0}</td>
                      <td className="text-end">{Number(row.total ?? 0) || 0}</td>
                      
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      )}

      {/* Most lifetime value hosts — sortable by value, users, orders, tickets, views */}
      <Card className="shadow-sm mb-4">
        <div
          className="d-flex align-items-center gap-2 py-2 px-3 border-bottom"
          style={{ cursor: "pointer" }}
          onClick={() => setCardCollapsed((c) => ({ ...c, ltv: !c.ltv }))}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setCardCollapsed((c) => ({ ...c, ltv: !c.ltv }))}
        >
          <span className="text-muted small" style={{ width: "1.25rem" }}>
            {cardCollapsed.ltv ? "▶" : "▼"}
          </span>
          <h6 className="mb-0">Most lifetime value hosts</h6>
        </div>
        {!cardCollapsed.ltv && (
        <Card.Body className="py-3 px-3">
          <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0 small text-muted">Sort by:</Form.Label>
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                value={ltvSortBy}
                onChange={(e) => setLtvSortBy(e.target.value)}
              >
                <option value="value">Value</option>
                <option value="users">Users</option>
                <option value="orders">Orders</option>
                <option value="tickets">Tickets</option>
                <option value="views">Views</option>
                <option value="eventCount">Event count</option>
              </Form.Select>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setLtvSortDesc((d) => !d)}
                title={ltvSortDesc ? "Descending (click for ascending)" : "Ascending (click for descending)"}
              >
                {ltvSortDesc ? "↓ Desc" : "↑ Asc"}
              </Button>
            </div>
          </div>
          {mostLtvHosts.length === 0 ? (
            <p className="text-muted mb-0 small">No organizations with lifetime value data yet. Use “Update event details” on an org to compute LTV.</p>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Organization</th>
                    <th className="text-end">Events</th>
                    <th className="text-end">Value</th>
                    <th className="text-end">Users</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Tickets</th>
                    <th className="text-end">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {mostLtvHosts.map((org, index) => (
                    <tr key={org.id}>
                      <td className="text-muted">{index + 1}</td>
                      <td>{org.name}</td>
                      <td className="text-end">{org.eventCount ?? "—"}</td>
                      <td className="text-end">
                        {org.lifetimeValue?.value != null
                          ? Number(org.lifetimeValue.value).toFixed(2)
                          : "—"}
                      </td>
                      <td className="text-end">{org.lifetimeValue?.users ?? "—"}</td>
                      <td className="text-end">{org.lifetimeValue?.orders ?? "—"}</td>
                      <td className="text-end">{org.lifetimeValue?.tickets ?? "—"}</td>
                      <td className="text-end">{org.lifetimeValue?.views ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        )}
      </Card>

      {totalWithEvents > 0 && (
        <Card className="shadow-sm mb-4">
          <div
            className="d-flex align-items-center gap-2 py-2 px-3 border-bottom"
            style={{ cursor: "pointer" }}
            onClick={() => setCardCollapsed((c) => ({ ...c, distribution: !c.distribution }))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setCardCollapsed((c) => ({ ...c, distribution: !c.distribution }))}
          >
            <span className="text-muted small" style={{ width: "1.25rem" }}>
              {cardCollapsed.distribution ? "▶" : "▼"}
            </span>
            <h6 className="mb-0">Organization Event Distribution</h6>
          </div>
          {!cardCollapsed.distribution && (
          <Card.Body className="py-3 px-3">
            <p className="text-muted mb-2 small">
              Based on {totalWithEvents} organizations with at least 1 event (from <code>event_count</code>).
            </p>
            <div className="d-flex flex-wrap gap-2">
              {eventCountDistribution.map(({ min, count, percent }) => (
                <Badge key={min} bg="primary" className="fs-7">
                  {min}+ events: {count} orgs{" "}
                  <span className="text-light-50">
                    ({percent.toFixed(1)}%)
                  </span>
                </Badge>
              ))}
            </div>
            {monthlyHostDistribution.length > 0 && (
              <div className="mt-3">
                <h6 className="mb-1 fs-6">New Hosts by Month</h6>
                <p className="text-muted mb-2 small">
                  Hosts first seen each month with <code>event_count ≥ 1</code>, and how many
                  of them are active (have at least one event in that month from{" "}
                  <code>lifetimeValue.allEvents</code>).
                </p>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="text-end">New hosts</th>
                        <th className="text-end">Total hosts (cumulative)</th>
                        <th className="text-end">Active hosts</th>
                        <th className="text-end">Active Percent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyHostDistributionWithCumulative.map((row) => {
                        const activeRow = activeOrgByMonth.find(
                          (r) => r.key === row.key
                        );
                        const activeCount = activeRow?.orgsTotal ?? 0;
                        const activePct = row.totalHostsToMonth
                          ? (activeCount / row.totalHostsToMonth) * 100
                          : 0;
                        return (
                          <tr
                            key={row.key}
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                selectedHostMonthKey === row.key
                                  ? "rgba(25, 135, 84, 0.05)"
                                  : undefined,
                            }}
                            onClick={() => {
                              setSelectedHostMonthKey((prev) =>
                                prev === row.key ? null : row.key
                              );
                              //reculculate
                              let activeCount = 0;
                              allOrgs.forEach((org) => {
                                const events = org.lifetimeValue?.allEvents || [];
                                if (!Array.isArray(events) || events.length === 0) return;
                                let hasInMonth = false;
                                events.forEach((ev) => {
                                  const ts = ev?.timestamp;
                                  const d =
                                    ts instanceof Date
                                      ? ts
                                      : ts?.toDate?.()
                                      ? ts.toDate()
                                      : ts
                                      ? new Date(ts)
                                      : null;
                                    if (!d || isNaN(d.getTime())) return;
                                    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                                      2,
                                      "0"
                                    )}`;
                                    if (key === row.key) {
                                      hasInMonth = true;
                                    }
                                    
                                  });
                                  if (!hasInMonth) return;
                                  // Exclude overlaps: do not include orgs whose createdAt month is the same
                                  activeCount++;
                                  return true;

                              });
                              setSelectedYearForEventsPopup(null);
                              setSelectedMonthForActivePopup(row.key);
                            }}
                          >
                            <td>
                              <span className="text-success text-decoration-underline">
                                {row.label}
                              </span>
                            </td>
                            <td className="text-end">{row.count}</td>
                            <td className="text-end">{row.totalHostsToMonth}</td>
                            <td className="text-end">{activeCount - row.count}</td>
                            <td className="text-end">{activePct.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
            {selectedHostMonthKey && orgsForSelectedHostMonth.length > 0 && (
              <div className="mt-3">
                <h6 className="mb-1 fs-6">
                  New hosts created in{" "}
                  {
                    monthlyHostDistribution.find(
                      (row) => row.key === selectedHostMonthKey
                    )?.label
                  }
                </h6>
                <p className="text-muted mb-2 small">
                  Organizations with <code>event_count ≥ 1</code> whose{" "}
                  <code>createdAt</code> month matches this row.
                </p>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Organization</th>
                        <th>Org ID</th>
                        <th className="text-end">Events</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgsForSelectedHostMonth.map((org, index) => (
                        <tr key={org.id}>
                          <td className="text-muted">{index + 1}</td>
                          <td>{org.name}</td>
                          <td>
                            <span className="font-monospace small">{org.id}</span>
                          </td>
                          <td className="text-end">
                            <Badge bg="primary">{org.eventCount}</Badge>
                          </td>
                          <td>{formatDateTime(org.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
            <div className="mt-3">
              <h6 className="mb-1 fs-6">Active Organizations (by month)</h6>
              <p className="text-muted mb-2 small">
                Number of organizations that had at least one event active in each month, based on{" "}
                <code>lifetimeValue.allEvents[].timestamp</code>. We show both the raw count and the
                count after removing overlaps with <strong>New Hosts by Month</strong> (same month as{" "}
                <code>createdAt</code>).
              </p>
              {activeOrgByMonth.length === 0 ? (
                <p className="text-muted small mb-0">
                  No active-organization data yet. Run <strong>Update event details</strong> (or bulk
                  update) to populate <code>lifetimeValue.allEvents</code>.
                </p>
              ) : (
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="text-end">Organizations (raw)</th>
                        <th className="text-end">Organizations (minus overlaps)</th>
                        <th className="text-end">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeOrgByMonth.map((row) => {
                        const pct = totalWithEvents
                          ? (row.orgs / totalWithEvents) * 100
                          : 0;
                        return (
                          <tr
                            key={row.key}
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                selectedFirstEventMonthKey === row.key
                                  ? "rgba(13, 110, 253, 0.05)"
                                  : undefined,
                            }}
                            onClick={() => {
                              setSelectedFirstEventMonthKey((prev) =>
                                prev === row.key ? null : row.key
                              );
                              setSelectedMonthForActivePopup(row.key);
                            }}
                          >
                            <td>
                              <span className="text-primary text-decoration-underline">
                                {row.label}
                              </span>
                            </td>
                            <td className="text-end">{row.orgsTotal ?? row.orgs}</td>
                            <td className="text-end">{row.orgs}</td>
                            <td className="text-end">{pct.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
            {selectedFirstEventMonthKey && orgsForSelectedFirstEventMonth.length > 0 && (
              <div className="mt-3">
                <h6 className="mb-1 fs-6">
                  Active organizations in{" "}
                  {
                    activeOrgByMonth.find(
                      (row) => row.key === selectedFirstEventMonthKey
                    )?.label
                  }
                </h6>
                <p className="text-muted mb-2 small">
                  Organizations that had at least one event in this month (from{" "}
                  <code>lifetimeValue.allEvents</code>), excluding those whose{" "}
                  <code>createdAt</code> month is the same (already counted as new hosts).
                </p>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Organization</th>
                        <th>Org ID</th>
                        <th className="text-end">Events</th>
                        <th className="text-end">First event</th>
                        <th>First event ID</th>
                        <th>First event title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgsForSelectedFirstEventMonth.map((org, index) => (
                        <tr key={org.id}>
                          <td className="text-muted">{index + 1}</td>
                          <td>{org.name}</td>
                          <td>
                            <span className="font-monospace small">{org.id}</span>
                          </td>
                          <td className="text-end">
                            <Badge bg="primary">{org.eventCount}</Badge>
                          </td>
                          <td className="text-end">
                            {org.lifetimeValue?.firstEventCreatedAt
                              ? formatDateTime(org.lifetimeValue.firstEventCreatedAt)
                              : "—"}
                          </td>
                          <td>
                            {org.lifetimeValue?.firstEventId ? (
                              <span className="font-monospace small">
                                {org.lifetimeValue.firstEventId}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{org.lifetimeValue?.firstEventTitle ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </Card.Body>
          )}
        </Card>
      )}

      {/* Organization view — organizations with more than one event */}
      <Card className="shadow-sm mb-4">
        <div
          className="d-flex justify-content-between align-items-center gap-2 py-2 px-3 border-bottom"
          style={{ cursor: "pointer" }}
          onClick={() => setCardCollapsed((c) => ({ ...c, orgView: !c.orgView }))}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setCardCollapsed((c) => ({ ...c, orgView: !c.orgView }))}
        >
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small" style={{ width: "1.25rem" }}>
              {cardCollapsed.orgView ? "▶" : "▼"}
            </span>
            <h6 className="mb-0">Organization view</h6>
          </div>
          <Button
            size="sm"
            variant="outline-success"
            disabled={loading || orgs.length === 0}
            onClick={(e) => {
              e.stopPropagation();
              handleExportOrganizationView();
            }}
          >
            Export names + creation date
          </Button>
        </div>
        {!cardCollapsed.orgView && (
        <Card.Body className="p-0">
          {loading ? (
            <div className="py-4 text-center">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading organizations...</span>
            </div>
          ) : orgs.length === 0 ? (
            <div className="py-4 text-center text-muted">
              No organizations found with more than one event.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Organization</th>
                    <th>Org ID</th>
                    <th>Events</th>
                    <th>Created</th>
                    <th>First event created</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((org, index) => (
                    <tr key={org.id}>
                      <td className="text-muted">{index + 1}</td>
                      <td>{org.name}</td>
                      <td>
                        <span className="font-monospace small">{org.id}</span>
                      </td>
                      <td>
                        <Badge bg="primary">{org.eventCount}</Badge>
                      </td>
                      <td>{formatDateTime(org.createdAt)}</td>
                      <td>
                        {org.lifetimeValue?.firstEventCreatedAt
                          ? formatDateTime(org.lifetimeValue.firstEventCreatedAt)
                          : "—"}
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={!!updatingEventDetailsId}
                          onClick={() => handleUpdateEventDetails(org)}
                        >
                          {updatingEventDetailsId === org.id
                            ? "Updating..."
                            : "Update event details"}
                        </Button>
                      </td>
                     
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        )}
      </Card>

      {/* All organizations ordered by timestamp */}
      <Card className="shadow-sm">
        <div
          className="d-flex align-items-center gap-2 py-2 px-3 border-bottom"
          style={{ cursor: "pointer" }}
          onClick={() => setCardCollapsed((c) => ({ ...c, allOrgs: !c.allOrgs }))}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setCardCollapsed((c) => ({ ...c, allOrgs: !c.allOrgs }))}
        >
          <span className="text-muted small" style={{ width: "1.25rem" }}>
            {cardCollapsed.allOrgs ? "▶" : "▼"}
          </span>
          <h6 className="mb-0">All Organizations (by created time)</h6>
        </div>
        {!cardCollapsed.allOrgs && (
        <Card.Body className="p-0">
          {loading ? (
            <div className="py-4 text-center">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading organizations...</span>
            </div>
          ) : allOrgs.length === 0 ? (
            <div className="py-4 text-center text-muted">
              No organizations found.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Organization</th>
                    <th>Org ID</th>
                    <th>Events</th>
                    <th>Created</th>
                    <th className="text-end">LTV value</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Users</th>
                    <th className="text-end">Views</th>
                    <th className="text-end">Tickets</th>
                    <th>LTV updated</th>
                    <th>Last event created</th>
                    <th>Last event</th>
                    <th>Last event ID</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {allOrgsPageItems.map((org, index) => (
                    <tr key={org.id}>
                      <td className="text-muted">
                        {allOrgsStartIndex + index + 1}
                      </td>
                      <td>{org.name}</td>
                      <td>
                        <span className="font-monospace small">{org.id}</span>
                      </td>
                      <td>
                        <Badge bg={org.eventCount > 0 ? "primary" : "secondary"}>
                          {org.eventCount}
                        </Badge>
                      </td>
                      <td>{formatDateTime(org.createdAt)}</td>
                      <td className="text-end">
                        {org.lifetimeValue?.value != null
                          ? Number(org.lifetimeValue.value).toFixed(2)
                          : "—"}
                      </td>
                      <td className="text-end">
                        {org.lifetimeValue?.orders != null ? org.lifetimeValue.orders : "—"}
                      </td>
                      <td className="text-end">
                        {org.lifetimeValue?.users != null ? org.lifetimeValue.users : "—"}
                      </td>
                      <td className="text-end">
                        {org.lifetimeValue?.views != null ? org.lifetimeValue.views : "—"}
                      </td>
                      <td className="text-end">
                        {org.lifetimeValue?.tickets != null ? org.lifetimeValue.tickets : "—"}
                      </td>
                      <td>
                        {org.lifetimeValue?.timestamp
                          ? formatDateTime(org.lifetimeValue.timestamp)
                          : "—"}
                      </td>
                      <td>
                        {org.lifetimeValue?.lastEventCreatedAt
                          ? formatDateTime(org.lifetimeValue.lastEventCreatedAt)
                          : "—"}
                      </td>
                      <td>{org.lifetimeValue?.lastEventTitle ?? "—"}</td>
                      <td>
                        {org.lifetimeValue?.lastEventId ? (
                          <span className="font-monospace small">{org.lifetimeValue.lastEventId}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={!!updatingOrgId}
                          onClick={() => handleUpdateEventCount(org)}
                          className="me-1"
                        >
                          {updatingOrgId === org.id ? "Updating..." : "Update"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={!!updatingEventDetailsId}
                          onClick={() => handleUpdateEventDetails(org)}
                        >
                          {updatingEventDetailsId === org.id
                            ? "Updating..."
                            : "Update event details"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {allOrgs.length > allOrgsPageSize && (
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
              <span className="small text-muted">
                Showing {allOrgsStartIndex + 1}-
                {Math.min(allOrgsStartIndex + allOrgsPageSize, allOrgs.length)} of{" "}
                {allOrgs.length}
              </span>
              <Pagination className="mb-0">
                <Pagination.First
                  disabled={allOrgsPageClamped === 1}
                  onClick={() => setAllOrgsPage(1)}
                />
                <Pagination.Prev
                  disabled={allOrgsPageClamped === 1}
                  onClick={() =>
                    setAllOrgsPage((p) => Math.max(1, p - 1))
                  }
                />
                <Pagination.Item active>{allOrgsPageClamped}</Pagination.Item>
                <Pagination.Next
                  disabled={allOrgsPageClamped === allOrgsTotalPages}
                  onClick={() =>
                    setAllOrgsPage((p) =>
                      Math.min(allOrgsTotalPages, p + 1)
                    )
                  }
                />
                <Pagination.Last
                  disabled={allOrgsPageClamped === allOrgsTotalPages}
                  onClick={() => setAllOrgsPage(allOrgsTotalPages)}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
        )}
      </Card>

      <Modal
        show={selectedYearForEventsPopup != null}
        onHide={() => setSelectedYearForEventsPopup(null)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Hosts & event counts – {selectedYearForEventsPopup}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-2">
            Showing <strong>{hostsAndEventsForSelectedYear.length}</strong> hosts and{" "}
            <strong>{totalEventsForSelectedYear}</strong> events for the year{" "}
            <code>{selectedYearForEventsPopup}</code> (from{" "}
            <code>lifetimeValue.allEvents[*].timestamp</code>).
          </p>
          {topHostByUsersForSelectedYear && (
            <p className="small mb-3">
              Top host by users:{" "}
              <strong>{topHostByUsersForSelectedYear.orgName}</strong> (
              <code>{topHostByUsersForSelectedYear.orgId}</code>) with{" "}
              <strong>{topHostByUsersForSelectedYear.usersInYear}</strong>{" "}
              users.
            </p>
          )}

          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Host</th>
                  <th>Org ID</th>
                  <th className="text-end">Events in year</th>
                  <th className="text-end">Users in year</th>
                </tr>
              </thead>
              <tbody>
                {hostsAndEventsForSelectedYear.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted text-center py-3">
                      No matching hosts for this year.
                    </td>
                  </tr>
                ) : (
                  hostsAndEventsForSelectedYear.map((host) => (
                    <tr key={host.orgId}>
                      <td>{host.orgName}</td>
                      <td>
                        <code className="small">{host.orgId}</code>
                      </td>
                      <td className="text-end">
                        {host.events?.length ?? 0}
                      </td>
                      <td className="text-end">
                        {Number(host.usersInYear ?? 0) || 0}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {selectedYearForEventsPopup === 2026 &&
            hostsAndEventsForSelectedYear.length > 0 && (
              <>
                <h6 className="fs-6 mt-3">Event details (2026)</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Host</th>
                        <th>Org ID</th>
                        <th>Event ID</th>
                        <th>Event title</th>
                        <th>Timestamp</th>
                        <th className="text-end">Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hostsAndEventsForSelectedYear.flatMap((host) =>
                        (host.events || []).map((ev) => (
                          <tr
                            key={`${host.orgId}-${ev.eventId}-${ev.timestamp.getTime()}`}
                          >
                            <td>{host.orgName}</td>
                            <td>
                              <code className="small">{host.orgId}</code>
                            </td>
                            <td>
                              <code className="small">{ev.eventId}</code>
                            </td>
                            <td>{ev.title}</td>
                            <td>{formatDateTime(ev.timestamp)}</td>
                            <td className="text-end">{Number(ev.users ?? 0) || 0}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!selectedMonthForActivePopup}
        onHide={() => setSelectedMonthForActivePopup(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Active hosts –{" "}
            {activeOrgByMonth.find(
              (r) => r.key === selectedMonthForActivePopup
            )?.label ||
              monthlyHostDistribution.find(
                (r) => r.key === selectedMonthForActivePopup
              )?.label ||
              selectedMonthForActivePopup}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-1">
            Organizations that had at least one event in this month (from{" "}
            <code>lifetimeValue.allEvents</code>).
          </p>
          <p className="small mb-3">
            <strong>{activeHostsListForPopup.length}</strong> active hosts,{" "}
            <strong>{newHostsListForPopup.length}</strong> new hosts in this month.
          </p>

          <h6 className="fs-6">Active hosts</h6>
          <div className="table-responsive mb-3">
            <Table hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Org ID</th>
                </tr>
              </thead>
              <tbody>
                {activeHostsListForPopup.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-muted text-center py-3">
                      No active hosts for this month.
                    </td>
                  </tr>
                ) : (
                  activeHostsListForPopup.map((org) => (
                    <tr key={org.id}>
                      <td>{org.name}</td>
                      <td>
                        <code className="small">{org.id}</code>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <h6 className="fs-6">New hosts (created this month)</h6>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Org ID</th>
                </tr>
              </thead>
              <tbody>
                {newHostsListForPopup.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-muted text-center py-3">
                      No new hosts created this month.
                    </td>
                  </tr>
                ) : (
                  newHostsListForPopup.map((org) => (
                    <tr key={org.id}>
                      <td>{org.name}</td>
                      <td>
                        <code className="small">{org.id}</code>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

